import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useParticipationRacks, useRackMutations, downloadRackQr } from '../api';
import { useScope } from '../../store/scope';
import { PageLoader, ErrorState, EmptyState, StatusBadge, Spinner } from '../../components/ui';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useToast } from '../../components/Toast';
import { ApiRequestError, http } from '../../lib/api';
import { num } from '../../lib/format';
import type { QrRack } from '../../lib/types';

/** Renders a rack QR: the stored image URL if present, else an authed PNG fetch. */
function QrThumb({ rack }: { rack: QrRack }) {
  const stored = rack.qrImage?.url ?? null;
  const [src, setSrc] = useState<string | null>(stored);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (stored) {
      setSrc(stored);
      return;
    }
    let url: string | null = null;
    let alive = true;
    setSrc(null);
    setFailed(false);
    http
      .get(`/racks/${rack._id}/qr`, { params: { format: 'png', size: 360 }, responseType: 'blob' })
      .then((res) => {
        if (!alive) return;
        url = URL.createObjectURL(res.data as Blob);
        setSrc(url);
      })
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [rack._id, stored]);

  if (failed) return <div className="flex h-full items-center justify-center text-xs text-ink-300">no image</div>;
  if (!src) return <div className="flex h-full items-center justify-center text-ink-300"><Spinner className="h-5 w-5" /></div>;
  return <img src={src} alt={`QR for ${rack.rackNumber}`} className="h-full w-full object-contain" />;
}

export function QRCodesPage() {
  const { participationId } = useScope();
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [regen, setRegen] = useState<QrRack | null>(null);
  const [busyId, setBusyId] = useState('');

  const { data, isLoading, error, refetch } = useParticipationRacks(participationId, {
    search: search || undefined,
    status: status || undefined,
  });
  // Regenerate needs a stall-scoped mutation hook; stallId varies per row so we
  // build the hook without one and target the rack by id.
  const rackMut = useRackMutations(undefined);

  const rows = useMemo(() => data?.data ?? [], [data]);

  const download = async (r: QrRack) => {
    setBusyId(r._id);
    try {
      await downloadRackQr(r._id, r.rackNumber);
    } catch {
      toast('Download failed', 'error');
    } finally {
      setBusyId('');
    }
  };

  if (!participationId) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold text-ink-900">QR Codes</h1>
        <EmptyState
          title="Choose an expo first"
          hint="Use the “Expo focus” selector in the top bar to see that expo's rack QR codes."
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-ink-900">QR Codes</h1>
          <p className="text-sm text-ink-400">
            {data ? `${num(rows.length)} racks` : ' '} · one QR per rack. Print and place on the rack.
          </p>
        </div>
        <button className="btn-outline print:hidden" onClick={() => window.print()}>
          Print sheet
        </button>
      </div>

      <div className="card flex flex-wrap gap-2 p-3 print:hidden">
        <input
          className="input max-w-xs"
          placeholder="Rack number / name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select className="input w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          <option value="active">active</option>
          <option value="inactive">inactive</option>
        </select>
      </div>

      {isLoading ? (
        <PageLoader />
      ) : error ? (
        <ErrorState message={(error as Error).message} onRetry={() => refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No racks in this expo"
          hint="Add stalls and racks from the expo's page — each rack gets a QR automatically."
        />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {rows.map((r) => (
            <div key={r._id} className="card flex flex-col items-center p-4 text-center">
              <div className="flex w-full items-center justify-between">
                <span className="text-xs text-ink-400">
                  {r.stallNumber ?? '—'} / {r.rackNumber}
                </span>
                <StatusBadge status={r.status} />
              </div>
              <div className="my-3 h-40 w-40 overflow-hidden rounded-lg border border-ink-100 bg-white">
                <QrThumb rack={r} />
              </div>
              <p className="text-sm font-medium text-ink-800">{r.rackName || r.rackNumber}</p>
              {r.qrUrl && (
                <a
                  href={r.qrUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-0.5 break-all font-mono text-[10px] text-gold-600 hover:underline"
                >
                  {r.qrUrl}
                </a>
              )}
              <p className="mt-1 text-xs text-ink-400">{num(r.assignedProductCount)} products · {num(r.scanCount)} scans</p>
              <div className="mt-3 flex gap-1 print:hidden">
                <button className="btn-ghost px-2 py-1 text-xs" disabled={busyId === r._id} onClick={() => download(r)}>
                  {busyId === r._id ? '…' : 'Download'}
                </button>
                <button className="btn-ghost px-2 py-1 text-xs" onClick={() => setRegen(r)}>
                  Regenerate
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <p className="text-xs text-ink-400 print:hidden">
        Need to add or remove racks?{' '}
        <Link to={`/admin/participations/${participationId}`} className="text-gold-600 hover:underline">
          Manage stalls &amp; racks
        </Link>
        .
      </p>

      <ConfirmDialog
        open={!!regen}
        title="Regenerate QR code?"
        tone="primary"
        confirmLabel="Regenerate"
        busy={rackMut.generateQr.isPending}
        message={
          regen && (
            <p>
              A new token is issued for <b>{regen.rackNumber}</b>. Printed copies of the old code stop working.
            </p>
          )
        }
        onCancel={() => setRegen(null)}
        onConfirm={async () => {
          try {
            await rackMut.generateQr.mutateAsync({ id: regen!._id, rotateToken: true });
            toast('QR regenerated', 'success');
            setRegen(null);
            refetch();
          } catch (e) {
            toast(e instanceof ApiRequestError ? e.message : 'Failed', 'error');
          }
        }}
      />
    </div>
  );
}
